export const DEFAULT_LOCATION = {
  latitude: parseFloat(import.meta.env.VITE_DEFAULT_LATITUDE) || -37.8136,
  longitude: parseFloat(import.meta.env.VITE_DEFAULT_LONGITUDE) || 144.9631, // Melbourne
};

export const LOCATION_PREFERENCE_KEY = 'dayforit_location_preference';
export const LAST_KNOWN_LOCATION_KEY = 'dayforit_last_location';
export const LOCATION_PREFERENCE = {
  allowed: 'allowed',
  denied: 'denied',
};

const LOCATION_TIMEOUT = parseInt(import.meta.env.VITE_LOCATION_TIMEOUT) || 10000; // 10 seconds to allow time for permission prompt
const LOCATION_MAX_AGE = 30 * 60 * 1000; // Reuse the browser's last fix for up to 30 minutes

// GeolocationPositionError codes → our `source` values
const GEOLOCATION_ERROR_SOURCES = { 1: 'denied', 2: 'unavailable', 3: 'timeout' };

export const getStoredLocationPreference = () => {
  try {
    return localStorage.getItem(LOCATION_PREFERENCE_KEY);
  } catch (error) {
    console.warn('Error reading location preference:', error);
    return null;
  }
};

export const setStoredLocationPreference = (preference) => {
  try {
    localStorage.setItem(LOCATION_PREFERENCE_KEY, preference);
  } catch (error) {
    console.warn('Error writing location preference:', error);
  }
};

/**
 * The last precise location the browser gave us, used to start fetching weather straight away
 * on the next visit while a fresh fix is on its way. Null if none (or storage is unavailable).
 */
export const getLastKnownLocation = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(LAST_KNOWN_LOCATION_KEY));
    return Number.isFinite(stored?.latitude) && Number.isFinite(stored?.longitude)
      ? { latitude: stored.latitude, longitude: stored.longitude }
      : null;
  } catch {
    return null;
  }
};

export const setLastKnownLocation = ({ latitude, longitude }) => {
  try {
    localStorage.setItem(LAST_KNOWN_LOCATION_KEY, JSON.stringify({ latitude, longitude }));
  } catch (error) {
    console.warn('Error writing last known location:', error);
  }
};

/**
 * Ask the browser for the current position. Never rejects.
 * @returns {Promise<{ location, source }>} source is 'browser' on success, otherwise
 *   'denied' (the user said no), 'unavailable', 'timeout' or 'unsupported', with DEFAULT_LOCATION.
 */
export const getCurrentLocationResult = () => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      console.warn("Geolocation is not supported by this browser. Using default.");
      resolve({ location: DEFAULT_LOCATION, source: 'unsupported' });
      return;
    }

    // Set a timeout in case the user doesn't respond to the permission prompt
    const timeoutId = setTimeout(() => {
      console.warn("Geolocation request timed out. Using default location.");
      resolve({ location: DEFAULT_LOCATION, source: 'timeout' });
    }, LOCATION_TIMEOUT);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(timeoutId);
        resolve({
          location: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          },
          source: 'browser',
        });
      },
      (error) => {
        clearTimeout(timeoutId);
        console.warn("Error getting location:", error.message);
        resolve({ location: DEFAULT_LOCATION, source: GEOLOCATION_ERROR_SOURCES[error.code] ?? 'unavailable' });
      },
      {
        enableHighAccuracy: false,
        timeout: LOCATION_TIMEOUT,
        maximumAge: LOCATION_MAX_AGE,
      }
    );
  });
};
